import { useState } from 'react'
import { ArrowLeft, Crosshair, Link2, Trash2 } from 'lucide-react'

import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import { Switch } from 'components/ui/switch'
import { Textarea } from 'components/ui/textarea'

import { LAYOUTS, TRANSITIONS } from '../constants'
import type { UseDeckEditor } from '../hooks/useDeckEditor'
import type { AssetName } from '../hooks/useAssetNames'
import type { BlockView } from '../hooks/useResolvedBlocks'
import { LAYOUT_SPECS, REGION_LABELS, regionsOf } from '../layouts'
import type { Block, DeckDefinition, ElementDescriptor, EntityRef, Slide, Story, Transition } from '../types/presentation.types'
import { AnchorChip } from './AnchorChip'
import { AssetPicker } from './AssetPicker'
import { ConfigForm } from './ConfigForm'
import { FrameMapDialog, type RegionSize } from './FrameMapDialog'
import { EXPLORER_QUERY_FIELDS, ExplorerTableInspector } from './inspector/ExplorerTableInspector'
import { SlideDraftControl } from './inspector/SlideDraftControl'

/**
 * The right pane (MYD-495). Shows the selected BLOCK when there is one, and
 * otherwise the selected slide, its story and the deck.
 *
 * Every edit goes through `useDeckEditor`, so every edit is undoable. Text
 * fields coalesce into one undo step per run of typing (see the hook).
 */

interface InspectorProps {
	editor: UseDeckEditor
	deck: DeckDefinition
	story: Story | null
	slide: Slide | null
	block: Block | null
	/** The selected block's resolved content, for controls that show it (Frame this view). */
	resolvedBlock?: BlockView
	/** The deck is shared with partners, who see only what they can open (MYD-502). */
	sharedDeck?: boolean
	/** The deck's id when Ask MDT may draft notes and text for it (MYD-640); absent, no draft buttons. */
	draftDeckId?: string | null
	elements: Record<string, ElementDescriptor>
	assetName: (ref: EntityRef | null) => AssetName | null
	onDeselectBlock: () => void
	onChangeLayout: (slideId: string) => void
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
	<section className="space-y-3 border-b border-gray-200 px-4 py-4 last:border-b-0">
		<h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
		{children}
	</section>
)

const Field = ({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) => (
	<div className="space-y-1.5">
		<Label htmlFor={htmlFor} className="text-xs font-medium text-gray-600">
			{label}
		</Label>
		{children}
	</div>
)

export function Inspector(props: InspectorProps) {
	const { block } = props
	if (block) return <BlockPanel {...props} block={block} />
	if (props.slide || props.story) return <SlidePanel {...props} />
	return <p className="px-4 py-6 text-sm text-muted-foreground">Add a story to begin.</p>
}

// =============================================================================
// Block
// =============================================================================

function BlockPanel({ editor, story, slide, block, resolvedBlock, sharedDeck = false, draftDeckId, elements, assetName, onDeselectBlock }: InspectorProps & { block: Block }) {
	const element = elements[block.element]
	const [changingSource, setChangingSource] = useState(false)
	const drawsOnAsset = Boolean(element && element.scopes.length > 0)
	const effectiveSource = block.source ?? story?.anchor ?? null

	return (
		<div>
			<button type="button" onClick={onDeselectBlock} className="flex items-center gap-1 px-4 pt-3 text-xs font-medium text-primary hover:underline">
				<ArrowLeft className="h-3.5 w-3.5" /> Slide settings
			</button>

			<Section title="Element">
				<div>
					<p className="text-sm font-semibold text-gray-900">{element?.label ?? block.element}</p>
					{element?.description && <p className="text-xs text-gray-500">{element.description}</p>}
				</div>

				{slide && (
					<Field label="Area" htmlFor="block-region">
						<Select id="block-region" value={block.region} onChange={event => editor.moveBlock(block.id, { region: event.target.value })}>
							{regionsOf(slide.layout).map(region => (
								<option key={region} value={region}>
									{REGION_LABELS[region] ?? region}
								</option>
							))}
						</Select>
					</Field>
				)}

				{drawsOnAsset && (
					<div className="space-y-2">
						<p className="text-xs font-medium text-gray-600">Draws from</p>
						{/* Evaluations are never shared with partners anywhere in the
						    product, so in a shared deck this block is theirs to see only
						    as "Not available" (MYD-502). Say so before they present it. */}
						{sharedDeck && effectiveSource?.scope === 'evaluation' && (
							<p className="rounded-md bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-800" role="note">
								Only you can see evaluation blocks. Partners viewing this shared deck will see “Not available” here.
							</p>
						)}
						<div className="flex flex-wrap items-center gap-2">
							<AnchorChip anchor={effectiveSource} name={assetName(effectiveSource)} />
							{block.source ? (
								<button type="button" onClick={() => editor.setBlockSource(block.id, null)} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
									<Link2 className="h-3 w-3" /> Use the story's asset
								</button>
							) : (
								<span className="text-[11px] text-muted-foreground">(the story's asset)</span>
							)}
							<button type="button" onClick={() => setChangingSource(open => !open)} className="text-xs text-primary hover:underline">
								{changingSource ? 'Cancel' : 'Change'}
							</button>
						</div>
						{changingSource && element && (
							<AssetPicker
								value={null}
								scopes={element.scopes}
								onChange={asset => {
									if (!asset) return
									editor.setBlockSource(block.id, { scope: asset.scope, entityId: asset.entityId })
									setChangingSource(false)
								}}
							/>
						)}
					</div>
				)}
			</Section>

			{element?.rasterize === 'map' && <MapFraming editor={editor} block={block} resolvedBlock={resolvedBlock} />}

			{element?.key === 'explorer.table' && (
				<Section title="Table">
					<ExplorerTableInspector config={block.config} onChange={config => editor.setBlockConfig(block.id, config)} />
				</Section>
			)}

			<Section title="Settings">
				{draftDeckId && slide && element?.key === TEXT_ELEMENT && (
					<SlideDraftControl
						key={block.id}
						presentationId={draftDeckId}
						target="text"
						slide={slide}
						story={story}
						blockId={block.id}
						onApply={markdown => editor.setBlockConfig(block.id, { ...block.config, markdown }, { ownStep: true })}
					/>
				)}
				{element ? (
					<ConfigForm
						idPrefix={`block-${block.id}`}
						schema={settingsSchema(element)}
						value={block.config}
						source={effectiveSource}
						onChange={config => editor.setBlockConfig(block.id, config)}
					/>
				) : (
					<p className="text-sm text-muted-foreground">This element is not available to you, so it cannot be edited here.</p>
				)}
			</Section>

			<div className="px-4 py-4">
				<button
					type="button"
					onClick={() => {
						editor.deleteBlock(block.id)
						onDeselectBlock()
					}}
					className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive hover:underline"
				>
					<Trash2 className="h-3.5 w-3.5" /> Remove from slide
				</button>
			</div>
		</div>
	)
}

// =============================================================================
// Map framing (MYD-500)
// =============================================================================

/** The block's size on the 1280x720 stage, measured on the canvas (offset sizes ignore the stage's scale). */
const measureRegion = (blockId: string): RegionSize | null => {
	const el = document.querySelector<HTMLElement>(`[data-slide-canvas] [data-block-id="${CSS.escape(blockId)}"]`)
	return el && el.offsetWidth > 0 && el.offsetHeight > 0 ? { width: el.offsetWidth, height: el.offsetHeight } : null
}

function MapFraming({ editor, block, resolvedBlock }: { editor: UseDeckEditor; block: Block; resolvedBlock?: BlockView }) {
	const [region, setRegion] = useState<RegionSize | null>(null)
	const framed = Boolean(block.config.camera)
	const payload = resolvedBlock?.status === 'ok' ? resolvedBlock.payload : null

	return (
		<Section title="View">
			<p className="text-xs text-gray-500">
				{framed ? 'The slide shows the view you framed.' : 'The slide fits the map to what it shows. Frame it to choose the view yourself.'}
			</p>
			<div className="flex flex-wrap items-center gap-3">
				<button
					type="button"
					disabled={!payload}
					onClick={() => setRegion(measureRegion(block.id) ?? { width: 720, height: 560 })}
					className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
				>
					<Crosshair className="h-3.5 w-3.5" /> Frame this view
				</button>
				{framed && (
					<button
						type="button"
						onClick={() => editor.setBlockConfig(block.id, withoutCamera(block.config))}
						className="text-xs text-primary hover:underline"
					>
						Fit automatically
					</button>
				)}
			</div>
			{region && (
				<FrameMapDialog
					open
					onClose={() => setRegion(null)}
					element={block.element}
					payload={payload}
					region={region}
					onSave={camera => editor.setBlockConfig(block.id, { ...block.config, camera })}
				/>
			)}
		</Section>
	)
}

/**
 * The fields the generic settings form draws: everything except what a
 * dedicated panel above it edits (a map's camera, an Explorer table's query).
 */
const settingsSchema = (element: ElementDescriptor) => {
	if (element.rasterize === 'map') return withoutCamera(element.configSchema)
	if (element.key === 'explorer.table') {
		const owned: readonly string[] = EXPLORER_QUERY_FIELDS
		return Object.fromEntries(Object.entries(element.configSchema).filter(([key]) => !owned.includes(key))) as ElementDescriptor['configSchema']
	}
	return element.configSchema
}

/**
 * A config or schema without its `camera`. The camera is set by framing, not
 * typed, so the settings form leaves it out, and "Fit automatically" drops it.
 */
const withoutCamera = <T extends Record<string, unknown>>(value: T): T =>
	Object.fromEntries(Object.entries(value).filter(([key]) => key !== 'camera')) as T

// =============================================================================
// Slide, story and deck
// =============================================================================

const TRANSITION_LABELS: Record<Transition, string> = { flip: 'Page flip', slide: 'Slide', fade: 'Fade', none: 'None' }

/** The element "Draft text" writes (MYD-640): the one whose content is the user's own markdown. */
const TEXT_ELEMENT = 'generic.text'

function SlidePanel({ editor, deck, story, slide, draftDeckId, assetName, onChangeLayout }: InspectorProps) {
	const [changingAnchor, setChangingAnchor] = useState(false)

	return (
		<div>
			{slide && (
				<Section title="Slide">
					<Field label="Layout" htmlFor="slide-layout">
						<div className="flex gap-2">
							<Select
								id="slide-layout"
								value={slide.layout}
								onChange={event => editor.setLayout(slide.id, event.target.value as Slide['layout'])}
							>
								{LAYOUTS.map(layout => (
									<option key={layout} value={layout}>
										{LAYOUT_SPECS[layout].label}
									</option>
								))}
							</Select>
							<button type="button" onClick={() => onChangeLayout(slide.id)} className="shrink-0 rounded-md border border-gray-200 px-2 text-xs text-gray-700 hover:bg-gray-50">
								Browse
							</button>
						</div>
						{slide.layout === 'section' && <p className="text-[11px] text-muted-foreground">A section slide shows its title only.</p>}
					</Field>
					<Field label="Title" htmlFor="slide-title">
						<Input id="slide-title" value={slide.title} maxLength={200} onChange={event => editor.updateSlide(slide.id, { title: event.target.value })} />
					</Field>
					<Field label="Subtitle" htmlFor="slide-subtitle">
						<Input id="slide-subtitle" value={slide.subtitle} maxLength={300} onChange={event => editor.updateSlide(slide.id, { subtitle: event.target.value })} />
					</Field>
					<Field label="Speaker notes" htmlFor="slide-notes">
						<Textarea
							id="slide-notes"
							rows={4}
							value={slide.speakerNotes}
							maxLength={10000}
							placeholder="Only you see these while presenting."
							onChange={event => editor.updateSlide(slide.id, { speakerNotes: event.target.value })}
						/>
						{draftDeckId && (
							<SlideDraftControl
								key={slide.id}
								presentationId={draftDeckId}
								target="notes"
								slide={slide}
								story={story}
								onApply={speakerNotes => editor.updateSlide(slide.id, { speakerNotes }, { ownStep: true })}
							/>
						)}
					</Field>
					<Field label="Transition into this slide" htmlFor="slide-transition">
						<Select
							id="slide-transition"
							value={slide.transition ?? ''}
							onChange={event => editor.updateSlide(slide.id, { transition: (event.target.value || null) as Transition | null })}
						>
							<option value="">Deck default ({TRANSITION_LABELS[deck.transition]})</option>
							{TRANSITIONS.map(transition => (
								<option key={transition} value={transition}>
									{TRANSITION_LABELS[transition]}
								</option>
							))}
						</Select>
					</Field>
				</Section>
			)}

			{story && (
				<Section title="Story">
					<Field label="Story title" htmlFor="story-title">
						<Input id="story-title" value={story.title} maxLength={200} onChange={event => editor.updateStory(story.id, { title: event.target.value })} />
					</Field>
					<div className="space-y-2">
						<p className="text-xs font-medium text-gray-600">About</p>
						<div className="flex flex-wrap items-center gap-2">
							<AnchorChip anchor={story.anchor} name={assetName(story.anchor)} />
							<button type="button" onClick={() => setChangingAnchor(open => !open)} className="text-xs text-primary hover:underline">
								{changingAnchor ? 'Cancel' : story.anchor ? 'Change' : 'Choose an asset'}
							</button>
							{story.anchor && (
								<button type="button" onClick={() => editor.updateStory(story.id, { anchor: null })} className="text-xs text-gray-500 hover:underline">
									Clear
								</button>
							)}
						</div>
						<p className="text-[11px] text-muted-foreground">Blocks that use the story's asset follow it when you change it.</p>
						{changingAnchor && (
							<AssetPicker
								value={null}
								onChange={asset => {
									if (!asset) return
									editor.updateStory(story.id, { anchor: { scope: asset.scope, entityId: asset.entityId } })
									setChangingAnchor(false)
								}}
							/>
						)}
					</div>
					<div className="flex items-start justify-between gap-3">
						<div>
							<Label htmlFor="story-divider" className="text-xs font-medium text-gray-600">
								Open with a divider slide
							</Label>
							<p className="text-[11px] text-muted-foreground">When presenting, shows the asset's name, address and photo before this story.</p>
						</div>
						<Switch
							id="story-divider"
							checked={story.divider === true}
							onChange={event => editor.updateStory(story.id, { divider: event.target.checked })}
						/>
					</div>
				</Section>
			)}

			<Section title="Presentation">
				<Field label="Transition" htmlFor="deck-transition">
					<Select id="deck-transition" value={deck.transition} onChange={event => editor.setDeckTransition(event.target.value as Transition)}>
						{TRANSITIONS.map(transition => (
							<option key={transition} value={transition}>
								{TRANSITION_LABELS[transition]}
							</option>
						))}
					</Select>
				</Field>
				<p className="text-[11px] text-muted-foreground">Applies to every slide that does not set its own.</p>
			</Section>
		</div>
	)
}

export default Inspector
